import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {DEFAULT_DESIGN_UI_STATE, IDesignUIState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {LocalStorageUtil} from '@mintdata-wsp/common/util/LocalStorageUtil';
import {ObjectUtils} from '@mintdata-wsp/mint-kit/impl/util/ObjectUtils';
import {SpreadsheetTabsView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetTabsView';
import bind from 'bind-decorator';
import _throttle from 'lodash/throttle';
import {action, autorun, observable} from 'mobx';
import {IDisposer} from 'mobx-utils';
import {controllers} from '../../WspControllers';
import {IWSPMeshContext} from '../spreadsheet/contexts/MeshContext';

export class DesignUIStateControler
{
    public readonly DESIGN_UI_STATE_LOCAL_STORAGE_KEY = 'wsp_design_ui_state';

    @observable
    private _isDesignUIStateWatching = false;

    private _designUIStateWatchDisposer:IDisposer = null;

    private _state:IDesignUIState;

    public setState(state:IDesignUIState)
    {
        this._state = state;
    }

    public loadAndApply(designId?:number):void
    {
        if( !designId )
            designId = controllers.ui.openDesign.getOpenDesign().designRef.designId;

        if( !designId )
            return;

        const designStatesItem = LocalStorageUtil.getItem(this.DESIGN_UI_STATE_LOCAL_STORAGE_KEY);

        if( !designStatesItem )
            return;

        let designStates = JSON.parse(designStatesItem);

        if( this._isDesignUIStateWatching )
            this.stopReaction();

        if( designStates[designId] )
            this.applyState(designStates[designId]);
        else
            this.applyState(DEFAULT_DESIGN_UI_STATE);
    }

    public applyState(state:IDesignUIState, mergeWithDefaults:boolean = false):void
    {
        const {spreadsheets, surfaceArea} = controllers.ui;
                
        const {
            workbench,
            topMenuState,
            surfaceEditor: {area},
            
        } = rootState.ui.visualDesigner;

        const surfaceState = surfaceArea.getState();

        const surfaceUIState = surfaceState ? surfaceState.uiState : null;

        const {main} = commonControllers.spreadsheets;

        let activeSheet = spreadsheets.main.getActiveSheet();

        let meshState:MeshState = null;
        let ctx:IWSPMeshContext = null;

        if( activeSheet )
        {
            ctx = spreadsheets.workbook.getMeshContextBySheetId(activeSheet.id);
            meshState = spreadsheets.mesh.getState(ctx);
        }

        const rootWorkbook = main.getRootWorkbook();

        const tabState = rootWorkbook ? spreadsheets.tabs.getTabState(rootWorkbook.id) : null;

        if( mergeWithDefaults )
        {
            state = ObjectUtils.merge(DEFAULT_DESIGN_UI_STATE, state);
        }

        workbench.fromDesignUIState(state.workbench);

        if( workbench.visibility.layout.isSurfaceCopyPagesPanelShown )
            controllers.ui.copyPages.refreshState();
    
        topMenuState.fromDesignUIState(state.topMenu);
        area.fromDesignUIState(state.surfaceArea);

        if( surfaceUIState )
            surfaceUIState.fromDesignUIState(state.surfaceUi);

        if( tabState )
        {
            tabState.fromDesignUIState(state.spreadsheetTabsState);

            const tabView:SpreadsheetTabsView = spreadsheets.tabs.getTabsViewInstance(rootWorkbook.id);

            if( tabView && state.spreadsheetTabsState.activeSheetId )
                tabView.activateTab(rootWorkbook.id, state.spreadsheetTabsState.activeSheetId);

            activeSheet = spreadsheets.main.getActiveSheet();
            ctx = spreadsheets.workbook.getMeshContextBySheetId(activeSheet.id);

            spreadsheets.mesh.focusMesh(ctx);
            spreadsheets.mesh.onFocusMesh(ctx);
            
            meshState = spreadsheets.mesh.getState(ctx);
        }

        if( meshState )
        {
            const {details} = state.meshState;

            meshState.fromDesignUIState(state.meshState);

            spreadsheets.meshSelection.selectCellRange(
                ctx,
                new MeshLocation(details.selectionRangeFrom.colIndex, details.selectionRangeFrom.rowIndex),
                new MeshLocation(details.selectionRangeTo.colIndex, details.selectionRangeTo.rowIndex)
            );
        }
    }

    @bind
    private _saveStateToLocalStorage(designId?:number):void
    {
        if( !designId )
        {
            const openDesign = controllers.ui.openDesign.getOpenDesign();

            if( !openDesign )
                return;

            designId = controllers.ui.openDesign.getOpenDesign().designRef.designId;
        }

        if( !designId )
            return;

        const designStatesItem = LocalStorageUtil.getItem(this.DESIGN_UI_STATE_LOCAL_STORAGE_KEY);

        let designStates = designStatesItem ? JSON.parse(designStatesItem) : {};

        designStates[designId] = this._state;

        LocalStorageUtil.setItem(this.DESIGN_UI_STATE_LOCAL_STORAGE_KEY, JSON.stringify(designStates));
    }

    private _throttledSaveStateToLocalStorage = _throttle(this._saveStateToLocalStorage, 1000);

    @action
    public enableReaction():void
    {
        this._isDesignUIStateWatching = true;

        if( !this._designUIStateWatchDisposer )
        {
            this._designUIStateWatchDisposer = autorun(() =>
            {
                const {designUIState, spreadsheets, surfaceArea} = controllers.ui;
                
                const {
                    workbench,
                    topMenuState,
                    surfaceEditor: {
                        area
                    },
                } = rootState.ui.visualDesigner;

                const surfaceState = surfaceArea.getState();

                if( !surfaceState )
                    return;

                const surfaceUIState = surfaceArea.getState().uiState;

                const {main} = commonControllers.spreadsheets;

                const activeSheet = spreadsheets.main.getActiveSheet();

                const ctx:IWSPMeshContext = spreadsheets.workbook.getMeshContextBySheetId(activeSheet.id);

                const meshState = spreadsheets.mesh.getState(ctx);
                const tabState = spreadsheets.tabs.getTabState(main.getRootWorkbook().id);
                
                // do not remove; required to trigger the autorun
                designUIState.setState({
                    workbench: workbench.toDesignUIState(),
                    topMenu: topMenuState.toDesignUIState(),
                    surfaceArea: area.toDesignUIState(),
                    surfaceUi: surfaceUIState.toDesignUIState(),
                    meshState: meshState.toDesignUIState(),
                    spreadsheetTabsState: tabState.toDesignUIState()
                });

                if( designUIState._isDesignUIStateWatching )
                    designUIState._throttledSaveStateToLocalStorage();
            });
        }
    }

    @action
    public stopReaction():void
    {
        this._isDesignUIStateWatching = false;

        if( this._designUIStateWatchDisposer )
            this._designUIStateWatchDisposer();

        this._designUIStateWatchDisposer = null;
    }
}